import {
  forwardRef,
  useId,
  type ReactNode,
  type SelectHTMLAttributes,
} from "react";
import { CaretDown } from "@phosphor-icons/react";
import { cn } from "../../utils/cn";

export interface SelectProps extends Omit<
  SelectHTMLAttributes<HTMLSelectElement>,
  "size"
> {
  label?: ReactNode;
  /** Helper text below the field; replaced by `error` when present. */
  hint?: ReactNode;
  /** Error message; also sets aria-invalid and the error border. */
  error?: ReactNode;
}

/* A labelled native select styled to match TextField, with a custom caret
 * (options come as <option> children). */
export const Select = forwardRef<HTMLSelectElement, SelectProps>(
  function Select(
    { label, hint, error, id, className, children, ...rest },
    ref,
  ) {
    const autoId = useId();
    const selectId = id ?? autoId;
    const messageId = `${selectId}-message`;
    const message = error ?? hint;

    return (
      <div className={cn("flex flex-col gap-1.5", className)}>
        {label && (
          <label
            htmlFor={selectId}
            className="text-body-sm font-medium text-fg-1"
          >
            {label}
          </label>
        )}
        <div className="relative">
          <select
            ref={ref}
            id={selectId}
            aria-invalid={error ? true : undefined}
            aria-describedby={message ? messageId : undefined}
            className={cn(
              "h-9 w-full appearance-none rounded-md border bg-surface pl-2.5 pr-8 text-body-sm text-fg-1",
              "outline-none transition-[border-color,box-shadow] duration-fast",
              "disabled:cursor-not-allowed disabled:opacity-50",
              error
                ? "border-error-border focus:border-error-border"
                : "focus:border-focus focus:shadow-focus",
            )}
            {...rest}
          >
            {children}
          </select>
          <CaretDown
            size={14}
            aria-hidden
            className="pointer-events-none absolute right-2.5 top-1/2 -translate-y-1/2 text-fg-2"
          />
        </div>
        {message && (
          <p
            id={messageId}
            className={cn(
              "text-body-sm",
              error ? "text-error-fg" : "text-fg-2",
            )}
          >
            {message}
          </p>
        )}
      </div>
    );
  },
);
